/**
 * <gj-recently-viewed> (handoff D2 §6, sections/gj-recently-viewed.liquid).
 * - localStorage `gj_recent`: product handles, newest first, max 12. On a product page the current handle moves to
 *   the front (after reading the list, so it is never shown on its own page).
 * - Cards: one Section Rendering request per product (/products/<handle>?section_id=gj-recent-card), in parallel,
 *   kept in visit order; products that no longer exist are dropped from the list.
 * - Fewer than data-min products → the section stays hidden (the inline script in the section already decided
 *   whether to reserve the space before the first paint).
 * - "Cancella cronologia" empties the list and hides the section.
 * localStorage can be missing or throw (private mode, blocked storage): every access is wrapped.
 */

const KEY = 'gj_recent';
const MAX = 12;

/** @returns {string[]} */
const read = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((item) => typeof item === 'string' && item) : [];
  } catch {
    return [];
  }
};

/** @param {string[]} list */
const write = (list) => {
  try {
    if (list.length) localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    else localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable: nothing to remember */
  }
};

class GjRecentlyViewed extends HTMLElement {
  connectedCallback() {
    const current = this.dataset.handle || '';
    const stored = read();
    const others = stored.filter((handle) => handle !== current);
    if (current) write([current, ...others]);

    this.querySelector('[data-gj-recent-clear]')?.addEventListener('click', this.#onClear);

    const limit = Number(this.dataset.limit) || MAX;
    const min = Number(this.dataset.min) || 1;
    const handles = others.slice(0, limit);
    if (handles.length < min) {
      this.#hide();
      return;
    }
    this.#load(handles, min);
  }

  disconnectedCallback() {
    this.querySelector('[data-gj-recent-clear]')?.removeEventListener('click', this.#onClear);
  }

  /** @param {string[]} handles @param {number} min */
  async #load(handles, min) {
    const base = this.dataset.url || '/products/';
    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${base}${encodeURIComponent(handle)}?section_id=gj-recent-card`);
          if (!response.ok) return { handle, item: null, gone: response.status === 404 };
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          return { handle, item: doc.querySelector('[data-gj-recent-item]'), gone: false };
        } catch {
          return { handle, item: null, gone: false };
        }
      })
    );

    const gone = results.filter((result) => result.gone).map((result) => result.handle);
    if (gone.length) write(read().filter((handle) => !gone.includes(handle)));

    const items = results.map((result) => result.item).filter((item) => item instanceof HTMLElement);
    const list = this.querySelector('[data-gj-recent-list]');
    if (!list || items.length < min) {
      this.#hide();
      return;
    }
    list.replaceChildren(...items.map((item) => document.importNode(item, true)));
    this.hidden = false;
    this.classList.remove('is-loading');
    // gj-slider: refresh arrows and progress for the new content
    list.dispatchEvent(new Event('scroll'));
  }

  #hide() {
    this.hidden = true;
    this.classList.remove('is-loading');
  }

  #onClear = () => {
    write([]);
    this.#hide();
  };
}

if (!customElements.get('gj-recently-viewed')) customElements.define('gj-recently-viewed', GjRecentlyViewed);
